import React from "react";
import { Box, Typography } from "@mui/material";
import { fmt } from "./utils";

// ── Request "type" label — drives both the badge and which Current
// Allocation fields to show (PSI-50) ────────────────────────────────────────
const requestTypeOf = (detail) => {
  if (detail.status === "MOVE_PENDING") return "Delimit and New Assign";
  if (detail.parentRequestId) {
    return detail.parentRequestStatus === "REJECTED"
      ? "Resubmitted"
      : "Amendment/Extension";
  }
  return "New Allocation";
};

const TYPE_COLOR = {
  "New Allocation":         "#4a9960",
  "Delimit and New Assign": "#D6DF27",
  "Amendment/Extension":    "#4a9960",
  "Resubmitted":            "#D6DF27",
};

const Field = ({ label, value, t, highlight }) => (
  <Box>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace", fontSize: "7px",
      letterSpacing: "0.12em", textTransform: "uppercase",
      color: t.text3, mb: "1px",
    }}>
      {label}
    </Typography>
    <Typography sx={{
      fontFamily: "'Inter', sans-serif", fontSize: "11px",
      color: highlight ? "#c84040" : t.text2, fontWeight: highlight ? 600 : 400,
    }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// ── Current Allocation field values, sourced differently per type ──────────
const currentFieldsFor = (detail) => {
  if (detail.status === "MOVE_PENDING" || (detail.parentRequestId && detail.parentRequestStatus !== "REJECTED")) {
    return {
      mac:          detail.parentMacName,
      sow:          detail.parentSowName,
      positionCode: detail.parentPositionCode,
      account:      detail.parentAccountName,
      fte:          detail.parentFte,
      status:       detail.parentIsBilled == null ? null : (detail.parentIsBilled ? "Billed" : "Unbilled"),
      duration:     `${fmt(detail.parentStartDate)} → ${fmt(detail.parentEndDate)}`,
    };
  }
  // New Allocation — no parent request to describe. currentMacName already
  // falls back to "SCG Pool" when the associate has no active allocation at
  // all, in which case every other field is genuinely empty (PSI-50).
  return {
    mac:          detail.currentMacName,
    sow:          detail.currentSowName ?? "—",
    positionCode: detail.currentPositionCode ?? "—",
    account:      detail.currentAccountName ?? "—",
    fte:          detail.currentFte ?? "—",
    status:       detail.currentIsBilled == null ? "Unbilled" : (detail.currentIsBilled ? "Billed" : "Unbilled"),
    duration:     detail.currentStartDate
      ? `${fmt(detail.currentStartDate)} → ${fmt(detail.currentEndDate)}`
      : "—",
  };
};

const AllocationComparisonCard = ({ detail, t, onNavigateToParent }) => {
  const type = requestTypeOf(detail);
  const current = currentFieldsFor(detail);
  const isBilled = detail.positionType === "BIL";

  // Only Move actually changes position/SOW/account — Amendment keeps the
  // same position, so its "current" values naturally equal "proposed" and
  // nothing renders red there, which is correct (only FTE/dates change).
  // "—" means we have nothing to compare against, not a difference.
  const changed = (from, to) => from != null && from !== "—" && from !== to;

  const macChanged      = changed(current.mac, detail.macName);
  const sowChanged      = changed(current.sow, detail.sowName);
  const positionChanged = changed(current.positionCode, detail.positionCode);
  const accountChanged  = changed(current.account, detail.accountName);
  const fteChanged      = changed(current.fte, detail.fte);
  const statusChanged   = changed(current.status, isBilled ? "Billed" : "Unbilled");
  const proposedPeriod  = `${fmt(detail.startDate)} → ${fmt(detail.endDate)}`;
  const periodChanged   = type !== "New Allocation" && changed(current.duration, proposedPeriod);

  return (
    <Box sx={{
      mt: "4px", padding: "10px 14px",
      background: t.surface2, border: `1px solid ${t.border2}`,
      borderRadius: "3px",
    }}>
      {/* Type badge */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
        <Box sx={{
          padding: "1px 7px", borderRadius: "2px",
          background: `${TYPE_COLOR[type]}18`, border: `1px solid ${TYPE_COLOR[type]}44`,
        }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace", fontSize: "8px",
            letterSpacing: "0.1em", textTransform: "uppercase",
            color: TYPE_COLOR[type], fontWeight: 700,
          }}>
            {type}
          </Typography>
        </Box>

        {detail.parentRequestId && (
          <Box
            onClick={onNavigateToParent}
            sx={{
              ml: "auto", cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: "9px",
              color: t.accent,
              textDecoration: "underline",
              textDecorationColor: `${t.accent}55`,
              "&:hover": { textDecorationColor: t.accent },
            }}
          >
            {detail.parentRequestCode} →
          </Box>
        )}
      </Box>

      {/* Current Allocation */}
      <Typography sx={{
        fontFamily: "'DM Mono', monospace", fontSize: "8px",
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: t.text3, mb: "8px", fontWeight: 700,
      }}>
        Current Allocation
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", mb: "14px" }}>
        <Field label="MAC"             value={current.mac}          t={t} />
        <Field label="Project/SOW Code" value={current.sow}          t={t} />
        <Field label="Position ID"     value={current.positionCode} t={t} />
        <Field label="Account"         value={current.account}      t={t} />
        <Field label="FTE"             value={current.fte}          t={t} />
        <Field label="Status"          value={current.status}       t={t} />
        <Field label="Duration"        value={current.duration}     t={t} />
      </Box>

      {/* Proposed */}
      <Typography sx={{
        fontFamily: "'DM Mono', monospace", fontSize: "8px",
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: t.accent, mb: "8px", fontWeight: 700,
      }}>
        Proposed
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
        <Field label="MAC"             value={detail.macName}                       t={t} highlight={macChanged} />
        <Field label="Project/SOW Code" value={detail.sowName}                       t={t} highlight={sowChanged} />
        <Field label="Position ID"     value={detail.positionCode}                  t={t} highlight={positionChanged} />
        <Field label="Account"         value={detail.accountName}                   t={t} highlight={accountChanged} />
        <Field label="Position Level"  value={detail.levelCode}                     t={t} />
        <Field label="FTE"             value={detail.fte}                           t={t} highlight={fteChanged} />
        <Field label="Status"          value={isBilled ? "Billed" : "Unbilled"}     t={t} highlight={statusChanged} />
        <Field label="Period"          value={proposedPeriod}                       t={t} highlight={periodChanged} />
        <Field label="Requester Name"  value={detail.submittedByName}               t={t} />
      </Box>

      {detail.notes && (
        <Box sx={{ mt: "12px" }}>
          <Field label="Remarks" value={detail.notes} t={t} />
        </Box>
      )}

      {/* Rejection reason if parent was rejected */}
      {detail.parentRejectionReason && (
        <Box sx={{ mt: "10px", padding: "6px 10px", background: "rgba(200,64,64,0.07)", border: "1px solid rgba(200,64,64,0.2)", borderRadius: "2px" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "7px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#c84040", mb: "2px" }}>
            Rejection Reason
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text2, lineHeight: 1.5 }}>
            {detail.parentRejectionReason}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AllocationComparisonCard;
